import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Size, TextInputType} from 'ui-library/inputs/InputConstants';
import TextInput, {TextInputProps} from 'ui-library/inputs/TextInput';

export interface SecureTextInputProps extends Omit<TextInputProps, 'onChange' | 'value' | 'parentType'> {
  disabled: boolean;
  onChange: (value: string) => void;
  name: string;
  parentType: string;
  hideNoLongerVisibleMessage?: boolean;
  value: string | null;
}

export default function SecureTextInput(props: SecureTextInputProps): JSX.Element | null {
  const [showKey, setShowKey] = React.useState(false);
  const [enteringValue, setEnteringValue] = React.useState(!props.value);
  const [newValue, setNewValue] = useState('');
  // Loaded an encrypted previously-stored value from the back end.
  const hasStoredValue = !enteringValue && props.value;

  function showOrHideKey(): void {
    setShowKey(!showKey);
  }

  function clearValue(): void {
    setEnteringValue(true);
    setNewValue('');
  }

  const showOrHideButton = (
    <Button
      buttonStyle={ButtonStyle.CIRCLE_ICON}
      size={ButtonSize.SMALL}
      className={css(styles.hideButton)}
      icon={showKey ? 'eye-slash' : 'eye'}
      onClick={showOrHideKey}
    />
  );

  // Show button with "X" icon to clear the stored value
  const clearButton = (
    <Button
      buttonStyle={ButtonStyle.CIRCLE_ICON}
      size={ButtonSize.SMALL}
      className={css(styles.closeButton)}
      onClick={clearValue}
      icon="cross-thick"
    />
  );

  const textInput = (
    <TextInput
      disabled={Boolean(hasStoredValue)}
      onChange={(event) => {
        if (enteringValue) {
          const inputValue = event.target.value;
          setNewValue(inputValue);
          props.onChange(inputValue);
        }
      }}
      // disable Copy secrets to clipboard from a secure_text field
      onCopy={(e: React.ClipboardEvent) => {
        e.preventDefault();
        return false;
      }}
      size={Size.SMALL}
      type={showKey ? TextInputType.TEXT : TextInputType.PASSWORD}
      value={enteringValue ? newValue : '********'}
      className={showKey ? '' : css(styles.secret)}
    />
  );
  return (
    <div className={css(styles.textParent)}>
      {hasStoredValue ? (
        <>
          <Tooltip
            text="This encrypted value cannot be viewed or edited."
            placement={FloatingPlacement.TOP}
            disableContentSelection={true}
          >
            <div>{textInput}</div>
          </Tooltip>
          <Tooltip text="Clear" placement={FloatingPlacement.TOP} disableContentSelection={true}>
            {clearButton}
          </Tooltip>
        </>
      ) : (
        <>
          {textInput}
          {(props.hideNoLongerVisibleMessage == null || !props.hideNoLongerVisibleMessage) && (
            <div className={css(styles.fieldDescription)}>
              After saving {props.parentType} this will no longer be visible.
            </div>
          )}
          <Tooltip
            text={showKey ? `Hide ${props.name}` : `Show ${props.name}`}
            placement={FloatingPlacement.TOP}
            disableContentSelection={true}
          >
            {showOrHideButton}
          </Tooltip>
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  secret: {
    fontSize: 20,
  },
  fieldDescription: {
    color: GRAY_DARKER,
  },
  textParent: {
    position: 'relative',
  },
  hideButton: {
    position: 'absolute',
    right: '1em',
    top: '0px',
  },
  closeButton: {
    position: 'absolute',
    right: '1em',
    top: '0px',
  },
});
